import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { MeResponse } from "../lib/api";
import { api } from "../lib/api";
import { signOut } from "../lib/auth";
import { VERCEL_AI_MODELS, type VercelAiModel } from "../lib/models";
import { FREE_MODELS, type UserRole } from "@re/shared";
import { EvolutionProposalCard } from "../components/research/EvolutionProposalCard";
import { Badge, Button, cn, Icon, Input, TierBadge } from "../components/ui";

const DEFAULT_MODEL_KEY = "re:default-model";
const DEFAULT_OUTPUT_LANG_KEY = "re:default-output-lang";

export const Route = createFileRoute("/settings")({ component: SettingsPage });

function SettingsPage() {
  const { t } = useTranslation();
  const me = useQuery({ queryKey: ["me"], queryFn: () => api.me(), retry: false });
  if (me.isLoading) return <p className="py-16 text-center text-ink-faint">{t("common.loading")}</p>;
  if (me.isError || !me.data) return <Navigate to="/login" />;
  return <Settings me={me.data} />;
}

type SettingsSection = "account" | "defaults" | "engine" | "users";

function Settings({ me }: { me: MeResponse }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [section, setSection] = useState<SettingsSection>("account");

  const devUpgrade = useMutation({
    mutationFn: () => api.devUpgrade(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });

  async function onSignOut() {
    await signOut();
    qc.clear();
    navigate({ to: "/" });
  }

  const sections: Array<{ id: SettingsSection; label: string; icon: "user" | "beaker" | "users" | "settings"; show: boolean }> = [
    { id: "account", label: t("settings.navAccount"), icon: "user", show: true },
    { id: "defaults", label: t("settings.navDefaults"), icon: "beaker", show: true },
    { id: "engine", label: "Engine", icon: "settings", show: me.role === "admin" },
    { id: "users", label: t("settings.navUsers"), icon: "users", show: me.role === "admin" },
  ];

  return (
    <main className="max-w-[1180px] mx-auto px-6 py-10">
      <header className="mb-9">
        <div className="text-micro uppercase font-medium text-ink-faint mb-2" style={{ letterSpacing: "0.08em" }}>
          {t("settings.eyebrow")}
        </div>
        <h1 className="font-serif text-[36px] leading-[1.1] font-medium text-ink tracking-tight">
          {t("settings.heading")}
        </h1>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10">
        <nav className="space-y-px">
          {sections.filter((s) => s.show).map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSection(s.id)}
              className={cn(
                "w-full flex items-center gap-2.5 h-9 px-3 rounded-md text-[13.5px] transition-colors text-left",
                section === s.id ? "bg-surface-2 text-ink" : "text-ink-dim hover:text-ink hover:bg-surface-2",
              )}
            >
              <Icon name={s.icon} size={14} />
              {s.label}
              {s.id === "users" && <Badge tone="line" size="xs" className="ml-auto">{t("common.admin")}</Badge>}
            </button>
          ))}
        </nav>

        <div className="space-y-12 max-w-[760px]">
          {section === "account" && (
            <AccountPanel
              me={me}
              onSignOut={onSignOut}
              onDevToggle={() => devUpgrade.mutate()}
              devPending={devUpgrade.isPending}
            />
          )}
          {section === "defaults" && <ResearchDefaults role={me.role} />}
          {section === "engine" && me.role === "admin" && <EnginePanel />}
          {section === "users" && me.role === "admin" && <AdminPanel />}
        </div>
      </div>
    </main>
  );
}

function SettingsPanel({ eyebrow, title, children }: { eyebrow: string; title: string; children: ReactNode }) {
  return (
    <section>
      <div className="text-micro uppercase font-medium text-ink-faint mb-1.5" style={{ letterSpacing: "0.08em" }}>
        {eyebrow}
      </div>
      <h2 className="font-serif text-[22px] font-medium text-ink tracking-tight mb-5">{title}</h2>
      <div className="border border-border rounded-lg bg-surface px-5">{children}</div>
    </section>
  );
}

function SettingsRow({
  label, hint, children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[1fr_360px] gap-6 py-5 border-b border-border last:border-0">
      <div>
        <div className="text-[14px] font-medium text-ink mb-1">{label}</div>
        {hint && <div className="text-[12.5px] text-ink-dim leading-snug">{hint}</div>}
      </div>
      <div className="flex items-center gap-3 justify-start sm:justify-end">{children}</div>
    </div>
  );
}

function AccountPanel({
  me, onSignOut, onDevToggle, devPending,
}: {
  me: MeResponse;
  onSignOut: () => void;
  onDevToggle: () => void;
  devPending: boolean;
}) {
  const { t } = useTranslation();
  return (
    <>
      <SettingsPanel eyebrow={t("settings.accountEyebrow")} title={t("settings.accountTitle")}>
        <SettingsRow label={t("settings.displayName")}>
          <div className="w-full text-[14px] text-ink-dim">{me.name}</div>
        </SettingsRow>
        <SettingsRow label={t("settings.email")} hint={t("settings.emailHint")}>
          <div className="w-full text-[14px] text-ink-dim font-mono break-all">{me.email}</div>
        </SettingsRow>
      </SettingsPanel>

      <SettingsPanel eyebrow={t("settings.planEyebrow")} title={t("settings.planTitle")}>
        <SettingsRow
          label={t("settings.currentPlan")}
          hint={me.role === "free" ? t("settings.freePlanHint") : t("settings.paidPlanHint")}
        >
          <div className="flex items-center gap-3 flex-wrap">
            <TierBadge role={me.role} />
            {import.meta.env.DEV && (
              <Button variant="ghost" size="sm" icon="sparkles" onClick={onDevToggle} disabled={devPending}>
                {devPending ? t("settings.devToggling") : t("settings.devToggle")}
              </Button>
            )}
          </div>
        </SettingsRow>
      </SettingsPanel>

      <SettingsPanel eyebrow={t("settings.sessionEyebrow")} title={t("settings.sessionTitle")}>
        <SettingsRow label={t("settings.signOut")} hint={t("settings.signOutHint")}>
          <Button variant="secondary" size="sm" icon="logout" onClick={onSignOut}>
            {t("settings.signOut")}
          </Button>
        </SettingsRow>
        <SettingsRow label={t("settings.deleteAccount")} hint={t("settings.deleteAccountHint")}>
          <Button variant="danger-ghost" size="sm" icon="trash" disabled>
            {t("settings.deleteAccount")}
          </Button>
        </SettingsRow>
      </SettingsPanel>
    </>
  );
}

function ResearchDefaults({ role }: { role: UserRole }) {
  const { t } = useTranslation();
  // Free plan can only default to a low-cost model (mirrors /new gating).
  const models =
    role === "free"
      ? VERCEL_AI_MODELS.filter((m) => (FREE_MODELS as readonly string[]).includes(m.id))
      : VERCEL_AI_MODELS;

  const [model, setModel] = useState<VercelAiModel>(() => {
    const saved = typeof window !== "undefined" ? window.localStorage.getItem(DEFAULT_MODEL_KEY) : null;
    return saved && models.some((m) => m.id === saved) ? (saved as VercelAiModel) : "deepseek-flash";
  });
  const [outputLanguage, setOutputLanguage] = useState(() => {
    const saved = typeof window !== "undefined" ? window.localStorage.getItem(DEFAULT_OUTPUT_LANG_KEY) : null;
    return saved && ["ko", "en", "ja", "zh", "es"].includes(saved) ? saved : "ko";
  });

  function onModelChange(next: VercelAiModel) {
    setModel(next);
    window.localStorage.setItem(DEFAULT_MODEL_KEY, next);
  }

  function onOutputLanguageChange(next: string) {
    setOutputLanguage(next);
    window.localStorage.setItem(DEFAULT_OUTPUT_LANG_KEY, next);
  }

  return (
    <SettingsPanel eyebrow={t("settings.defaultsEyebrow")} title={t("settings.defaultsTitle")}>
      <SettingsRow
        label={t("settings.defaultModel")}
        hint={`${t("settings.defaultModelHint")}${role === "free" ? ` ${t("settings.defaultModelFreeHint")}` : ""}`}
      >
        <select
          className="h-9 px-3 bg-surface border border-border rounded-md text-[13px] w-full text-ink"
          value={model}
          onChange={(e) => onModelChange(e.target.value as VercelAiModel)}
        >
          {models.map((m) => (
            <option key={m.id} value={m.id}>{m.label}</option>
          ))}
        </select>
      </SettingsRow>
      <SettingsRow label={t("settings.outputLanguage")} hint={t("settings.outputLanguageHint")}>
        <select
          className="h-9 px-3 bg-surface border border-border rounded-md text-[13px] w-full text-ink"
          value={outputLanguage}
          onChange={(e) => onOutputLanguageChange(e.target.value)}
        >
          <option value="ko">ko</option>
          <option value="en">en</option>
          <option value="ja">ja</option>
          <option value="zh">zh</option>
          <option value="es">es</option>
        </select>
      </SettingsRow>
    </SettingsPanel>
  );
}

function EnginePanel() {
  const capabilities = useQuery({
    queryKey: ["research-capabilities"],
    queryFn: () => api.capabilities(),
    staleTime: 60_000,
    retry: false,
  });
  const proposals = useQuery({
    queryKey: ["evolution-proposals"],
    queryFn: () => api.listEvolutionProposals(),
    staleTime: 60_000,
    retry: false,
  });

  const unavailable = capabilities.data?.dependencies.filter((dep) => !dep.available) ?? [];
  const skillPacks = capabilities.data?.skill_packs ?? [];

  return (
    <SettingsPanel eyebrow="Engine" title="Research engine">
      <div className="py-5 space-y-6">
        {capabilities.data ? (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <EngineFact label="name" value={capabilities.data.engine.name} />
              <EngineFact label="generated" value={capabilities.data.generated_at} />
              {capabilities.data.engine.version && <EngineFact label="version" value={capabilities.data.engine.version} />}
              {capabilities.data.engine.git_ref && <EngineFact label="git" value={capabilities.data.engine.git_ref} />}
            </div>

            <div>
              <h3 className="text-[13px] font-medium text-ink mb-2">Runners</h3>
              <div className="flex flex-wrap gap-2">
                {capabilities.data.runners.map((runner) => (
                  <Badge key={runner.id} tone={runner.available ? "ok" : "danger"} size="sm">
                    {runner.label ?? runner.id}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <h3 className="text-[13px] font-medium text-ink mb-2">Unavailable dependencies</h3>
                {unavailable.length ? (
                  <ul className="space-y-1.5 text-[12.5px] text-ink-dim">
                    {unavailable.map((dep) => (
                      <li key={dep.id} className="font-mono break-all">
                        {dep.label ?? dep.id}{dep.detail ? `: ${dep.detail}` : ""}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-[12.5px] text-ink-faint">None reported</p>
                )}
              </div>
              <div>
                <h3 className="text-[13px] font-medium text-ink mb-2">Skill packs</h3>
                {skillPacks.length ? (
                  <ul className="space-y-1.5 text-[12.5px] text-ink-dim">
                    {skillPacks.map((pack) => (
                      <li key={`${pack.repo}-${pack.ref ?? pack.path ?? pack.status}`} className="font-mono break-all">
                        {pack.repo} <span className="text-ink-faint">{pack.status}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-[12.5px] text-ink-faint">None reported</p>
                )}
              </div>
            </div>
          </>
        ) : capabilities.isError ? (
          <p className="text-[13px] text-danger">{String(capabilities.error)}</p>
        ) : (
          <p className="text-[13px] text-ink-faint">Loading engine capabilities...</p>
        )}

        <div className="border-t border-border pt-5">
          <h3 className="text-[13px] font-medium text-ink mb-3">Evolution proposals</h3>
          {proposals.data?.length ? (
            <div className="grid gap-3">
              {proposals.data.map((proposal) => (
                <EvolutionProposalCard key={proposal.id} proposal={proposal} />
              ))}
            </div>
          ) : proposals.isError ? (
            <p className="text-[13px] text-danger">{String(proposals.error)}</p>
          ) : (
            <p className="text-[12.5px] text-ink-faint">{proposals.isLoading ? "Loading proposals..." : "No proposals"}</p>
          )}
        </div>
      </div>
    </SettingsPanel>
  );
}

function EngineFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-surface-2 p-3">
      <div className="text-[11px] uppercase text-ink-faint" style={{ letterSpacing: "0.08em" }}>{label}</div>
      <div className="mt-1 text-[12.5px] font-mono text-ink-dim break-all">{value}</div>
    </div>
  );
}

function AdminPanel() {
  const { t } = useTranslation();
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState("paid");
  const set = useMutation({
    mutationFn: () => api.setUserRole(userId.trim(), role),
  });

  return (
    <SettingsPanel eyebrow={t("admin.eyebrow")} title={t("admin.title")}>
      <div className="py-5 grid grid-cols-1 md:grid-cols-[1fr_160px_auto] gap-3 md:items-end">
        <label className="space-y-1">
          <span className="block text-[12px] font-medium text-ink-dim">{t("admin.userId")}</span>
          <Input
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder={t("admin.userIdPlaceholder")}
            className="font-mono"
          />
        </label>
        <label className="space-y-1">
          <span className="block text-[12px] font-medium text-ink-dim">{t("admin.role")}</span>
          <select className="h-10 px-3 bg-surface border border-border rounded-md text-[13px] w-full text-ink" value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="free">free</option>
            <option value="paid">paid</option>
            <option value="admin">admin</option>
          </select>
        </label>
        <Button
          variant="accent"
          onClick={() => set.mutate()}
          disabled={!userId.trim() || set.isPending}
        >
          {set.isPending ? t("admin.changing") : t("admin.change")}
        </Button>
      </div>
      {set.isSuccess && <p className="pb-5 text-sm text-ok">{t("admin.changed")}</p>}
      {set.isError && <p className="pb-5 text-sm text-danger">{t("admin.failed", { error: String(set.error) })}</p>}
    </SettingsPanel>
  );
}
